import { useCallback, useEffect } from 'react';
import { createStore, createHook } from 'react-sweet-state';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers';
import { useSidebar } from '@atlassian/jira-business-app-wrapper';
import { VIEW_MODE_MODAL, VIEW_MODE_SIDEBAR } from '@atlassian/jira-issue-context-service';

const localStorage = createLocalStorageProvider('jira_business_issue_view');
export const STORAGE_KEY = 'jwm.issue-app-mode';

type State = {
	openInSidebar: boolean;
	onSwitchToSidebar?: () => unknown;
	onSwitchToModal?: () => unknown;
};

const actions = {
	switchToSidebar:
		() =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				openInSidebar: true,
			});

			localStorage.set(STORAGE_KEY, VIEW_MODE_SIDEBAR);
		},
	switchToModal:
		() =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			setState({
				...state,
				openInSidebar: false,
			});

			localStorage.set(STORAGE_KEY, VIEW_MODE_MODAL);
		},

	initState:
		() =>
		({ setState, getState }: { setState: (arg1: State) => void; getState: () => State }) => {
			const state = getState();
			const storageValue = localStorage.get(STORAGE_KEY);

			const nextOpenInSidebar =
				typeof storageValue !== 'undefined' ? storageValue === VIEW_MODE_SIDEBAR : false;

			if (state.openInSidebar !== nextOpenInSidebar) {
				setState({
					...state,
					openInSidebar: nextOpenInSidebar,
				});
			}
		},
} as const;

const Store = createStore<State, typeof actions>({
	initialState: {
		openInSidebar: false,
	},
	actions,
	name: 'JWMIssueAppSwitcherStore',
});

const useHook = createHook(Store);

/**
 * @deprecated Remove this hook as part of `refactor-bento-view-mode-logic` feature flag clean up
 */
export const useIssueAppSwitcher = () => {
	const [{ openInSidebar }, hookActions] = useHook();

	const [, { setSidebarContent }] = useSidebar();

	useEffect(() => {
		hookActions.initState();
	}, [hookActions]);

	const switchToModal = useCallback(() => {
		setSidebarContent(null);
		hookActions.switchToModal();
	}, [setSidebarContent, hookActions]);

	return [
		{ openInSidebar },
		{ switchToSidebar: hookActions.switchToSidebar, switchToModal },
	] as const;
};
